import { useEffect, useState } from 'react'

import { useEditFacility } from '../hooks/useEditFacility'
import { usePermissions } from '@/hooks/usePermissions'
import { useToasts } from '@/hooks/useToasts'
import { SubmitHandler, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { HiOutlineCheckCircle, HiOutlineOfficeBuilding } from 'react-icons/hi'

import {
  Box,
  FormErrorMessage,
  FormLabel,
  Grid,
  HStack,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalOverlay,
} from '@chakra-ui/react'

import { Modal } from '@/components/overlays'
import {
  AddressSearch,
  Button,
  FormInputControl,
  FormInputSelectControl,
  PinMarker,
  SkeletonModalBody,
  StaticMapBox,
} from '@/components/ui'
import { StyledFormInputControl } from '@/components/ui'
import { EmergencyCallConfigurationStatus } from '@/features/emergency-call'
import { FacilityType, UpdateFacilityInput } from '@/graphql/generated/schemas'
import { STRING_REGEX } from '@/utils/forms/validation'
import { tVar } from '@/utils/i18n'

import { FormInputsIProps, LatLngStateIProps } from '../types/types'
import {
  facilityTypeOptions,
  getErrorMessage,
  getFacilityTimezone,
  getFacilityTypeOption,
  latRegex,
  lonRegex,
} from '../utils/utils'
import { AddressChangeAlert } from './AddressChangeAlert'

interface EditFacilityModalIProps {
  facilityId: string
  isOpen: boolean
  onClose: () => void
}

export const EditFacilityModal = ({
  isOpen,
  onClose,
  facilityId,
}: EditFacilityModalIProps) => {
  const { t } = useTranslation()
  const {
    handleSubmit,
    errors,
    register,
    formState,
    setValue,
    control,
    watch,
  } = useForm({
    mode: 'onChange',
  })
  const { isValid, isSubmitting } = formState
  const [latLng, setLatLng] = useState<LatLngStateIProps | null>(null)
  const [hasAddressChanged, setHasAddressChanged] = useState(false)
  const { latitude, longitude } = watch(['latitude', 'longitude'])
  const { showSuccess, showError } = useToasts()
  const { shouldEnableUpdateFacility, shouldEnableUpdateFacilityAddress } =
    usePermissions()
  const {
    update,
    facility,
    floors,
    defaultFloor,
    isLoading,
    error,
    isEmergencyCallEnabled,
    isEmergencyCallConfigured,
  } = useEditFacility(facilityId)

  const onSubmit: SubmitHandler<FormInputsIProps> = async (values) => {
    const type = values.type?.value
    const isMarketFacility = type === FacilityType.Market
    const input: UpdateFacilityInput = {
      id: facilityId,
      name: values.name,
      shortName: values.shortName.toLocaleUpperCase(),
      type,
      ...(!isMarketFacility && {
        // The properties does'nt apply to a market facility
        address: values?.address ? [values.address] : null,
        defaultFloorId: values?.defaultFloor?.value,
        emergencyNumber: values.emergencyNumber,
        latitude: parseFloat(values.latitude),
        longitude: parseFloat(values.longitude),
        timeZone: getFacilityTimezone(values.latitude, values.longitude),
      }),
    }
    try {
      await update(input)
      const successMsg = tVar(t, 'facilities:crud_success_updated_facility', {
        name: values.name,
      })
      showSuccess(successMsg)
      onClose()
    } catch (e) {
      showError(getErrorMessage(e, values.name, 'updating'))
    }
  }

  useEffect(() => {
    if (facility && !isLoading) {
      if (facility.type === FacilityType.Normal) {
        register('address', { required: t('facilities:crud_address_required') })
        register('latitude', {
          validate: (v) => {
            if (facility.type === FacilityType.Market) return true
            if (!v) return t('facilities:crud_latitude_required')
            if (!latRegex.test(v)) return t('facilities:crud_latitude_invalid')
            return true
          },
        })
        register('longitude', {
          validate: (v) => {
            if (facility.type === FacilityType.Market) return true
            if (!v) return t('facilities:crud_longitude_required')
            if (!lonRegex.test(v)) return t('facilities:crud_longitude_invalid')
            return true
          },
        })
      }

      setValue('name', facility.name, { shouldValidate: true })
      setValue('shortName', facility.shortName, { shouldValidate: true })
      setValue('address', facility.address?.join(', '), {
        shouldValidate: true,
      })
      setValue('emergencyNumber', facility.emergencyNumber, {
        shouldValidate: false,
      })
      setValue('latitude', facility.latitude, { shouldValidate: true })
      setValue('longitude', facility.longitude, { shouldValidate: true })
      setValue('tz', facility.timeZone)

      if (facility.latitude && facility.longitude) {
        setLatLng({ lat: facility.latitude, lng: facility.longitude })
      }
    }
  }, [facility, isLoading])

  useEffect(() => {
    if (latRegex.test(latitude) && lonRegex.test(longitude)) {
      setLatLng({
        lat: parseFloat(latitude),
        lng: parseFloat(longitude),
      })
      setValue('tz', getFacilityTimezone(latitude, longitude))
    }
  }, [latitude, longitude])

  return (
    <Modal
      id='facilitiesPage_editFacilityModal'
      isOpen={isOpen}
      motionPreset='slideInBottom'
      onClose={onClose}
      size='xl'
    >
      <ModalOverlay />
      <ModalContent bg='#fff' overflow='hidden'>
        <ModalCloseButton color='#000' opacity={0.4} />
        {error ? (
          <>
            <ModalBody p='0'>
              <Box px='10' py='6'>
                <Grid gap='10px' gridTemplateColumns='30px 1fr'>
                  <HiOutlineOfficeBuilding size='30px' />
                  <Box>
                    <Box
                      color='#353849'
                      fontSize='26px'
                      fontWeight='extrabold'
                      letterSpacing='-0.4px'
                      lineHeight='1'
                      mb='3'
                    >
                      {t('facilities:crud_edit_title')}
                    </Box>
                    <Box
                      color='#2D2E41'
                      fontSize='17px'
                      letterSpacing='-0.4px'
                      mt='3'
                    >
                      {t('facilities:crud_facility_not_found')}
                    </Box>
                  </Box>
                </Grid>
              </Box>
            </ModalBody>
            <ModalFooter>
              <Button
                data-testid='facilitiesPage_editFacilityModal_closeButton'
                onClick={onClose}
              >
                {t('common:close')}
              </Button>
            </ModalFooter>
          </>
        ) : (
          <>
            <ModalBody p='0'>
              {!facility || isLoading ? (
                <SkeletonModalBody />
              ) : (
                <>
                  <Box px='10' py='6'>
                    <Grid gap='10px' gridTemplateColumns='30px 1fr'>
                      <HiOutlineOfficeBuilding size='30px' />
                      <Box>
                        <Box
                          color='#353849'
                          fontSize='26px'
                          fontWeight='extrabold'
                          letterSpacing='-0.4px'
                          lineHeight='1'
                          mb='3'
                        >
                          {t('facilities:crud_edit_title')}
                        </Box>
                        <Box
                          color='#2D2E41'
                          fontSize='17px'
                          letterSpacing='-0.4px'
                          mt='3'
                        />
                      </Box>
                    </Grid>
                  </Box>
                  <Box>
                    <form>
                      <Box px='6'>
                        <Box mb='3'>
                          <FormInputControl
                            data-testid='EditFacilityModal:name'
                            errorMessage={errors.name && errors.name.message}
                            id='name'
                            inputRef={register({
                              required: t(
                                'facilities:crud_facility_name_required'
                              ),
                              pattern: {
                                value: STRING_REGEX,
                                message: t('common:insert_alphanumeric_only'),
                              },
                            })}
                            isInvalid={errors.name}
                            label={t('common:facility_name')}
                            placeholder={t(
                              'facilities:crud_facility_name_placeholder_edit'
                            )}
                          />
                        </Box>
                        <Box mb='3'>
                          <FormInputControl
                            data-testid='EditFacilityModal:shortName'
                            errorMessage={
                              errors.shortName && errors.shortName.message
                            }
                            id='shortName'
                            inputRef={register({
                              required: t(
                                'facilities:crud_short_name_required'
                              ),
                              maxLength: {
                                value: 8,
                                message: t(
                                  'facilities:crud_short_name_max_length'
                                ),
                              },
                            })}
                            isInvalid={errors.shortName}
                            label={t('facilities:crud_short_name_label')}
                            placeholder={t(
                              'facilities:crud_short_name_placeholder_edit'
                            )}
                          />
                        </Box>
                        <Box mb='3'>
                          <FormInputSelectControl
                            control={control}
                            dataTestId='createFacility_facilityName'
                            defaultValue={getFacilityTypeOption(facility)}
                            id='type'
                            isDisabled={true}
                            isInvalid={errors.type}
                            label={t('facilities:crud_facility_type_label')}
                            options={facilityTypeOptions}
                            placeholder={t(
                              'facilities:crud_facility_type_placeholder'
                            )}
                            rules={{ required: true }}
                          />
                        </Box>

                        {facility?.type === FacilityType.Normal &&
                          defaultFloor && (
                            <Box mb='3'>
                              <FormInputSelectControl
                                control={control}
                                dataTestId='createFacility_deafultFloor'
                                defaultValue={defaultFloor}
                                id='defaultFloor'
                                label={t('facilities:crud_default_floor_label')}
                                options={floors}
                                placeholder={t(
                                  'facilities:crud_default_floor_placeholder'
                                )}
                              />
                            </Box>
                          )}

                        {facility?.type === FacilityType.Normal && (
                          <StyledFormInputControl mb='4'>
                            <FormLabel>{t('common:address')}</FormLabel>
                            <AddressSearch
                              defaultValue={facility.address?.join(', ')}
                              disabled={!shouldEnableUpdateFacilityAddress}
                              onSelectAddress={(address) => {
                                if (address) {
                                  const originalAddress =
                                    facility.address?.join(', ')
                                  const hasAddressChanged =
                                    address.value !== originalAddress

                                  setValue('address', address.value, {
                                    shouldValidate: true,
                                  })
                                  setValue('latitude', address.latitude, {
                                    shouldValidate: true,
                                  })
                                  setValue('longitude', address.longitude, {
                                    shouldValidate: true,
                                  })
                                  setHasAddressChanged(hasAddressChanged)
                                }
                              }}
                              showTooltip={true}
                            />
                            <FormErrorMessage>
                              {errors.address && errors.address.message}
                            </FormErrorMessage>
                          </StyledFormInputControl>
                        )}

                        {facility?.type === FacilityType.Normal &&
                          shouldEnableUpdateFacilityAddress && (
                            <HStack align='stretch' mb='4' spacing='3'>
                              <FormInputControl
                                errorMessage={
                                  errors.latitude && errors.latitude.message
                                }
                                id='latitude'
                                inputRef={register()}
                                isInvalid={Boolean(errors.latitude)}
                                label={t('facilities:crud_latitude_label')}
                                placeholder={t(
                                  'facilities:crud_latitude_placeholder'
                                )}
                              />
                              <FormInputControl
                                errorMessage={
                                  errors.longitude && errors.longitude.message
                                }
                                id='longitude'
                                inputRef={register()}
                                isInvalid={Boolean(errors.longitude)}
                                label={t('facilities:crud_longitude_label')}
                                placeholder={t(
                                  'facilities:crud_longitude_placeholder'
                                )}
                              />
                            </HStack>
                          )}

                        {facility?.type === FacilityType.Normal && (
                          <Box mb='3'>
                            <FormInputControl
                              disabled={true}
                              id='tz'
                              inputRef={register()}
                              label={t('facilities:crud_time_zone_label')}
                            />
                          </Box>
                        )}

                        {isEmergencyCallConfigured && (
                          <Box mb='3'>
                            <FormInputControl
                              control={control}
                              data-testid='EditFacilityModal:emergencyNumber'
                              errorMessage={
                                errors.emergencyNumber &&
                                errors.emergencyNumber.message
                              }
                              id='emergencyNumber'
                              inputRef={register()}
                              isInvalid={errors.emergencyNumber}
                              label={t(
                                'facilities:crud_emergency_number_label'
                              )}
                              placeholder={t(
                                'facilities:crud_emergency_number_placeholder'
                              )}
                            />
                          </Box>
                        )}
                      </Box>
                      <EmergencyCallConfigurationStatus
                        isConfigured={isEmergencyCallConfigured}
                        isEnabled={isEmergencyCallEnabled}
                      />

                      <AddressChangeAlert
                        hasAddressChanged={hasAddressChanged}
                        isEmergencyCallConfigured={isEmergencyCallConfigured}
                        isEmergencyCallEnabled={isEmergencyCallEnabled}
                      />

                      {facility?.type !== FacilityType.Market && latLng && (
                        <StaticMapBox
                          height='150px'
                          latitude={latLng.lat}
                          longitude={latLng.lng}
                        >
                          <PinMarker
                            Icon={HiOutlineOfficeBuilding}
                            bgGradient='linear(to-r, #1CFDE5, #07DB94)'
                            borderColor='#42CD70'
                            fadedPulseBoxShadow='rgba(43, 210, 180, 0)'
                            pulseBoxShadow='rgba(43, 210, 180, 0.4)'
                          />
                        </StaticMapBox>
                      )}
                    </form>
                  </Box>
                </>
              )}
            </ModalBody>
            {facility && !isLoading && (
              <ModalFooter py='4'>
                <Button
                  data-testid='facilitiesPage_editFacilityModal_cancelButton'
                  mr='3'
                  onClick={onClose}
                  variant='secondary'
                >
                  {t('common:cancel')}
                </Button>
                <Button
                  data-testid='facilitiesPage_editFacilityModal_saveButton'
                  isDisabled={!isValid || !shouldEnableUpdateFacility}
                  isLoading={isSubmitting}
                  leftIcon={<HiOutlineCheckCircle size='24px' />}
                  loadingText={t('common:saving_changes')}
                  onClick={handleSubmit(onSubmit)}
                >
                  {t('common:save_changes')}
                </Button>
              </ModalFooter>
            )}
          </>
        )}
      </ModalContent>
    </Modal>
  )
}
